import { Popup } from '@vis.gl/react-maplibre';
import { useShallow } from 'zustand/react/shallow';

import { NoteInfoCard } from '@/components/notes/NoteInfoCard';
import { TaskInfoCard } from '@/components/tasks/TaskInfoCard';
import { useDeviceLayout } from '@/hooks';
import { useGeoMarkupStore } from '@/store';

import styles from './MarkerHoverPreview.module.css';

// Half marker-sprite height + small gap so the popup sits above the marker.
const POPUP_OFFSET_PX = 28;
const POPUP_MAX_WIDTH_PX = 320;
const POPUP_MAX_WIDTH = `${POPUP_MAX_WIDTH_PX}px`;

export function MarkerHoverPreview() {
  const { hasHover } = useDeviceLayout();
  // Marker hover (punktowe pin'y) ma priorytet nad geometry hoverem
  // (linie/kształty), bo trafienie markera to silniejszy sygnał intencji.
  const kind = useGeoMarkupStore(
    (state) =>
      (state.ui.hoveredMarkerPreview ?? state.ui.hoveredGeometryPreview)
        ?.kind ?? null
  );
  const id = useGeoMarkupStore(
    (state) =>
      (state.ui.hoveredMarkerPreview ?? state.ui.hoveredGeometryPreview)?.id ??
      null
  );
  const latitude = useGeoMarkupStore(
    (state) =>
      (state.ui.hoveredMarkerPreview ?? state.ui.hoveredGeometryPreview)
        ?.coordinates.lat ?? null
  );
  const longitude = useGeoMarkupStore(
    (state) =>
      (state.ui.hoveredMarkerPreview ?? state.ui.hoveredGeometryPreview)
        ?.coordinates.lng ?? null
  );

  const task = useGeoMarkupStore(
    useShallow((state) => {
      if (kind !== 'task' || id === null) return null;
      const mapTask = state.tasks.mapTasks.find(
        (candidate) => candidate.id === id
      );
      if (!mapTask) return null;
      return {
        title: mapTask.title,
        status: mapTask.status,
        priority: mapTask.priority,
        assignedToName: mapTask.assignedToName,
        assignedToAvatarUrl: mapTask.assignedToAvatarUrl,
        assignedToIsFormerMember: mapTask.assignedToIsFormerMember,
        dueDate: mapTask.dueDate,
        requirementCount: mapTask.requirementCount ?? 0,
        completedRequirementCount: mapTask.completedRequirementCount ?? 0,
      };
    })
  );

  const note = useGeoMarkupStore(
    useShallow((state) => {
      if (kind !== 'note' || id === null) return null;
      const mapNote = state.notes.mapNotes.find(
        (candidate) => candidate.id === id
      );
      if (!mapNote) return null;
      return {
        title: mapNote.title,
        color: mapNote.color,
        visibility: mapNote.visibility,
        creatorName: mapNote.creatorName,
        creatorAvatarUrl: mapNote.creatorAvatarUrl,
        createdAt: mapNote.createdAt,
        linkedTasks: mapNote.linkedTasks,
      };
    })
  );

  if (!hasHover || latitude === null || longitude === null) return null;
  const hasContent =
    (kind === 'task' && task !== null) || (kind === 'note' && note !== null);
  if (!hasContent) return null;

  return (
    <Popup
      longitude={longitude}
      latitude={latitude}
      anchor="bottom"
      offset={POPUP_OFFSET_PX}
      closeButton={false}
      closeOnClick={false}
      closeOnMove={false}
      maxWidth={POPUP_MAX_WIDTH}
      className={styles.popup}
    >
      <div className={styles.card}>
        {kind === 'task' && task !== null && <TaskInfoCard task={task} />}
        {kind === 'note' && note !== null && <NoteInfoCard note={note} />}
      </div>
    </Popup>
  );
}
